import { FiChevronLeft } from 'react-icons/fi';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import PieChartWidget from './PieChartWidget';
import { UsageTable } from './UsageTable';
import { Loader } from '@/components/custom/loader';

export function PlatformUsageReport() {
  const navigate = useNavigate();
  const [kpisData, setKpisData] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [regionals, setRegionals] = useState([]);
  const [loading, setLoading] = useState(false);

  const client = useClient();

  const filters = useFormik({
    initialValues: {
      'start-date': moment().subtract(30, 'days').format('YYYY-MM-DD'),
      'end-date': moment().format('YYYY-MM-DD'),
      regional: null,
      searchTerm: ''
    }
  });

  const kpis = [
    {
      id: 1,
      title: 'Usuários Cadastrados',
      value: kpisData?.['total_registered_users'] !== null ? kpisData?.['total_registered_users'] : '-',
      variation: 'report-kpi-card--users'
    },
    {
      id: 2,
      title: 'Usuários ativos',
      value: kpisData?.['active_users_last_30_days'] !== null ? kpisData?.['active_users_last_30_days'] : '-',
      variation: 'report-kpi-card--user'
    },
    {
      id: 3,
      title: 'Funcionalidade mais usada',
      value: kpisData?.['most_used_feature']?.['feature_label_pt'] !== null ? kpisData?.['most_used_feature']?.['feature_label_pt'] : '-',
      descriptionValue: `${kpisData?.['most_used_feature']?.['percent_of_total_users']}%`,
      variation: 'report-kpi-card--percent'
    }
  ];

  const getReportData = async () => {
    setLoading(true);
    let url = `/reports/usage?start_date=${filters.values['start-date']}&end_date=${filters.values['end-date']}`;

    if (filters.values.regional) {
      url += `&regional_id=${filters.values.regional}`;
    }

    try {
      const { data, error } = await client.fetch(url);
      if (error?.message === 'Parametros de data invalidos') {
        toast.error('O intervalo entre a data inicial e final deve ser de no máximo 30 dias');
        setLoading(false);
        setKpisData([]);
        setReportData([]);
        return;
      }
      setKpisData(data?.kpis || []);
      setReportData(data?.['usage_by_feature'] || []);
    } catch (error) {
      const errorMessage = 'Erro ao buscar os dados do relatório';
      toast.error(errorMessage);
    }
    setLoading(false);
  };

  const fetchRegionals = async (url = '/regionals') => {
    const { data, error } = await client.fetch(url);
    if (error) {
      toast.error('Falha ao carregar regionais');
    } else {
      setRegionals(data.map(item => ({ value: item.id, label: item.name })));
    }
  };

  useEffect(() => {
    fetchRegionals();
    getReportData();
  }, []);

  useEffect(() => {
    getReportData();
  }, [filters.values['start-date'], filters.values['end-date'], filters.values.regional]);

  const regionalOptions = regionals.map(item => ({ value: item.value, label: item.label }));

  return (
    <div className="report">
      <header className="report__header">
        <h1 className="report__title">
          <button
            className="card__icon-wrapper"
            aria-label="Voltar"
            title="Voltar"
            onClick={() => navigate('/metricas/institucional')}
          >
            <FiChevronLeft className="card__icon" />
          </button>
          Relatório de Uso da Plataforma
        </h1>
        <p className="report__description">Identifica as funcionalidades mais utilizadas pelos alunos, ajudando a entender padrões de uso na plataforma.</p>
      </header>

      <main className="report__body">
        <div
          className="report-filter__select"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex gap-3">
            <Select
              openMenuOnFocus
              options={[{ value: null, label: 'Todas as GREs' }, ...regionalOptions]}
              className="react-multi-select report-form__select"
              classNamePrefix="react-multi-select"
              placeholder={'Selecione a GRE'}
              noOptionsMessage={() => 'Sem opções'}
              components={{
                IndicatorSeparator: () => null,
                ClearIndicator: () => null
              }}
              onChange={e => filters.setFieldValue('regional', e.value)}
            />
            <div className="report-form__datepicker">
              <input
                className="report-form__control"
                id="start-date"
                name="start-date"
                type="date"
                value={moment(filters.values['start-date']).format('YYYY-MM-DD')}
                onChange={e => filters.setFieldValue('start-date', e.target.value)}
                title="Data de início"
                aria-label="Data de início"
                min={moment().startOf('year').format('YYYY-MM-DD')}
              />
              <input
                className="report-form__control"
                id="end-date"
                name="end-date"
                type="date"
                value={moment(filters.values['end-date']).format('YYYY-MM-DD')}
                onChange={e => filters.setFieldValue('end-date', e.target.value)}
                min={moment(filters.values['start-date']).format('YYYY-MM-DD')}
                title="Data de fim"
                aria-label="Data de fim"
              />
            </div>
          </div>
        </div>

        {loading && <Loader />}

        {!loading && (
          <div className="report-widget__body gap-12">
            <div className="report-kpi-card-wrapper">
              {kpis.map(kpi => (
                <div
                  className={`report-kpi-card ${kpi.variation}`}
                  key={kpi.id}
                >
                  <h3 className="report-kpi-card__title">{kpi.title}</h3>
                  <p
                    className="report-kpi-card__value"
                    title={kpi.value}
                  >
                    {kpi.value}
                  </p>
                  <p className="text-sm text-primary-dark">
                    <b>{kpi.descriptionValue}</b> dos usuários utilizam
                  </p>
                </div>
              ))}
            </div>

            <PieChartWidget
              filters={filters}
              data={reportData}
            />

            <UsageTable
              filters={filters}
              tableData={reportData}
            />
          </div>
        )}
      </main>
    </div>
  );
}
